/**
 * useSignupWizard Hook
 * Manages state for the simplified signup wizard.
 *
 * Flow:
 * 1. Email: enter email
 * 2. Verify: enter 6-digit code from email
 * 3. Password: create password (Cognito signUp + signIn)
 * 4. Welcome: 30-day free trial started, auto-redirect to /add
 */

import { useState, useCallback } from 'react';
import { validatePassword } from '@/lib/password';

// Wizard steps (4 total)
export type WizardStep = 1 | 2 | 3 | 4;

export interface SignupWizardState {
  // Navigation
  currentStep: WizardStep;

  // Step 1: Email
  email: string;

  // Step 2: Verify Email
  verificationCode: string;
  isEmailVerified: boolean;

  // Step 3: Password
  password: string;
  confirmPassword: string;

  // UI State
  isLoading: boolean;
  error: string | null;
}

const initialState: SignupWizardState = {
  currentStep: 1,
  email: '',
  verificationCode: '',
  isEmailVerified: false,
  password: '',
  confirmPassword: '',
  isLoading: false,
  error: null,
};

export interface UseSignupWizardReturn {
  state: SignupWizardState;
  // Navigation
  goToStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  canProceed: () => boolean;
  // Step 1
  setEmail: (email: string) => void;
  // Step 2
  setVerificationCode: (code: string) => void;
  setIsEmailVerified: (verified: boolean) => void;
  // Step 3
  setPassword: (password: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;
  // UI State
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  // Utilities
  reset: () => void;
}

export function useSignupWizard(): UseSignupWizardReturn {
  const [state, setState] = useState<SignupWizardState>(initialState);

  // Navigation
  const goToStep = useCallback((step: WizardStep) => {
    setState(prev => ({ ...prev, currentStep: step, error: null }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, 4) as WizardStep,
      error: null,
    }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => {
      const nextStep = Math.max(prev.currentStep - 1, 1) as WizardStep;
      return {
        ...prev,
        currentStep: nextStep,
        error: null,
        // Clear verification code when returning to verify step so the user starts fresh
        ...(prev.currentStep === 3 && nextStep === 2 ? { verificationCode: '', isEmailVerified: false } : {}),
      };
    });
  }, []);

  const canProceed = useCallback((): boolean => {
    const { currentStep, email, verificationCode, password, confirmPassword } = state;

    switch (currentStep) {
      case 1:
        // Email: valid email format
        return !!email && email.includes('@') && email.includes('.');

      case 2:
        // Verify: 6-digit code
        return verificationCode.length === 6;

      case 3:
        // Password: valid password + confirmation match
        if (!password || !confirmPassword) return false;
        if (password !== confirmPassword) return false;
        return validatePassword(password).isValid;

      case 4:
        // Welcome: always true
        return true;

      default:
        return false;
    }
  }, [state]);

  // Step 1
  const setEmail = useCallback((email: string) => {
    setState(prev => ({ ...prev, email }));
  }, []);

  // Step 2
  const setVerificationCode = useCallback((verificationCode: string) => {
    setState(prev => ({ ...prev, verificationCode }));
  }, []);

  const setIsEmailVerified = useCallback((isEmailVerified: boolean) => {
    setState(prev => ({ ...prev, isEmailVerified }));
  }, []);

  // Step 3
  const setPassword = useCallback((password: string) => {
    setState(prev => ({ ...prev, password }));
  }, []);

  const setConfirmPassword = useCallback((confirmPassword: string) => {
    setState(prev => ({ ...prev, confirmPassword }));
  }, []);

  // UI State
  const setIsLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    goToStep,
    nextStep,
    prevStep,
    canProceed,
    setEmail,
    setVerificationCode,
    setIsEmailVerified,
    setPassword,
    setConfirmPassword,
    setIsLoading,
    setError,
    reset,
  };
}
